/**
 * useSettings - Persistent app settings backed by localStorage
 *
 * Currently supports:
 * - maxCardsPerReview: max cards per review session (5–100, default 20)
 */

import { useState, useCallback } from 'react'

const STORAGE_KEY = 'settings'

const DEFAULTS = {
  maxCardsPerReview: 20,
} as const

const MIN_CARDS = 5
const MAX_CARDS = 100

function loadSettings(): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function persistSettings(settings: Record<string, unknown>): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
}

export interface UseSettingsReturn {
  maxCardsPerReview: number
  setMaxCardsPerReview: (value: number) => void
}

export function useSettings(): UseSettingsReturn {
  const [settings, setSettings] = useState<Record<string, unknown>>(loadSettings)

  const maxCardsPerReview = typeof settings.maxCardsPerReview === 'number'
    ? Math.min(Math.max(settings.maxCardsPerReview, MIN_CARDS), MAX_CARDS)
    : DEFAULTS.maxCardsPerReview

  const setMaxCardsPerReview = useCallback((value: number) => {
    const clamped = Math.min(Math.max(value, MIN_CARDS), MAX_CARDS)
    setSettings(prev => {
      const next = { ...prev, maxCardsPerReview: clamped }
      persistSettings(next)
      return next
    })
  }, [])

  return { maxCardsPerReview, setMaxCardsPerReview }
}
